import type Phaser from 'phaser';
import type { MvpCharacterId } from '../session/game-session';
import { STICK_PROFILES, createStickPose } from '../battle/stick-figure-model';
import type { StickPoint } from '../battle/stick-figure-model';
import { CHARACTER_SELECT_LAYOUT } from './character-select-layout';

export interface PreviewRenderOptions {
  readonly scale?: number;
  readonly playerSlot?: 'p1' | 'p2' | 'both' | 'cpu' | 'none';
  readonly frame?: number;
  readonly poseState?: 'idle' | 'win';
}

export class StickPreviewRenderer {
  public static draw(
    graphics: Phaser.GameObjects.Graphics,
    characterId: MvpCharacterId,
    rootX: number,
    rootY: number,
    options: PreviewRenderOptions = {},
  ): void {
    const scale = options.scale ?? 0.74;
    const playerSlot = options.playerSlot ?? 'none';
    const frame = options.frame ?? 0;
    const poseState = options.poseState ?? 'idle';
    const profile = STICK_PROFILES[characterId];
    const pose = createStickPose(profile, poseState, null, frame);

    const transform = (p: StickPoint): { x: number; y: number } => ({
      x: rootX + p.x * scale,
      y: rootY + p.y * scale,
    });

    const head = transform(pose.head);
    const neck = transform(pose.neck);
    const hip = transform(pose.hip);
    const leftHand = transform(pose.leftHand);
    const rightHand = transform(pose.rightHand);
    const leftFoot = transform(pose.leftFoot);
    const rightFoot = transform(pose.rightFoot);

    // Ground Contact Shadow / Ring
    const shadowColor = playerSlot === 'p1' ? CHARACTER_SELECT_LAYOUT.colors.p1Accent
      : playerSlot === 'p2' ? CHARACTER_SELECT_LAYOUT.colors.p2Accent
        : playerSlot === 'both' ? CHARACTER_SELECT_LAYOUT.colors.bothAccent
          : 0x1e293b;
    const shadowAlpha = playerSlot === 'none' ? 0.4 : 0.85;

    graphics.fillStyle(0x000000, 0.4);
    graphics.fillEllipse(rootX, rootY, 44 * scale, 12 * scale);
    if (playerSlot !== 'none') {
      graphics.lineStyle(2, shadowColor, shadowAlpha);
      graphics.strokeEllipse(rootX, rootY, 48 * scale, 14 * scale);
    }

    // Character Body
    const lineWidth = Math.max(3, profile.lineWidth * scale);
    const headRadius = profile.headRadius * scale;

    graphics.lineStyle(lineWidth, profile.bodyColor, 1);
    graphics.fillStyle(profile.bodyColor, 1);

    // Head
    graphics.strokeCircle(head.x, head.y, headRadius);

    // Torso & Limbs
    graphics.lineBetween(neck.x, neck.y, hip.x, hip.y);
    graphics.lineBetween(neck.x, neck.y, leftHand.x, leftHand.y);
    graphics.lineBetween(neck.x, neck.y, rightHand.x, rightHand.y);
    graphics.lineBetween(hip.x, hip.y, leftFoot.x, leftFoot.y);
    graphics.lineBetween(hip.x, hip.y, rightFoot.x, rightFoot.y);

    // Overhead Player Indicator (if player active)
    if (playerSlot !== 'none' && playerSlot !== 'cpu') {
      const markerColor = playerSlot === 'p1' ? CHARACTER_SELECT_LAYOUT.colors.p1Accent
        : playerSlot === 'p2' ? CHARACTER_SELECT_LAYOUT.colors.p2Accent
          : CHARACTER_SELECT_LAYOUT.colors.bothAccent;
      const markerY = head.y - headRadius - 6;
      graphics.fillStyle(markerColor, 0.95);
      graphics.fillTriangle(head.x - 5, markerY - 8, head.x + 5, markerY - 8, head.x, markerY);
    }

    // Character Prop / Weapon Accent
    graphics.fillStyle(profile.accentColor, 1);
    graphics.fillCircle(rightHand.x, rightHand.y, lineWidth * 0.7);

    if (pose.marker === 'DOCUMENTS') {
      graphics.fillStyle(0xe8f4ff, 0.95).fillRect(rightHand.x - 12 * scale, rightHand.y - 10 * scale, 24 * scale, 19 * scale)
        .lineStyle(1.5, profile.accentColor, 1).strokeRect(rightHand.x - 12 * scale, rightHand.y - 10 * scale, 24 * scale, 19 * scale);
    } else if (pose.marker === 'DEADLINE') {
      graphics.lineStyle(3 * scale, profile.accentColor, 1).strokeRect(rightHand.x - 15 * scale, rightHand.y - 15 * scale, 30 * scale, 30 * scale)
        .lineBetween(rightHand.x - 15 * scale, rightHand.y - 3 * scale, rightHand.x + 15 * scale, rightHand.y - 3 * scale);
    }
  }
}
